import React, { useState, useCallback, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import planGenerationService from '../../services/planGenerationService';
import PlanPreviewModal from './PlanPreviewModal';
import './PlantationPlanGenerator.css';

/**
 * Plantation Plan Generator Component
 * Handles the complete flow of generating 10-year plantation plans
 */
const PlantationPlanGenerator = ({ 
  spatialData, 
  isVisible, 
  onClose, 
  onPlanGenerated,
  assistantOpen
}) => {
  const [generationState, setGenerationState] = useState('idle'); // idle, validating, generating, cancelling, completed, error
  const [progress, setProgress] = useState(null);
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [streamedSections, setStreamedSections] = useState([]);
  const [showStreamingPreview, setShowStreamingPreview] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Validate spatial data when component mounts or spatial data changes
  useEffect(() => {
    if (spatialData && isVisible) {
      const validation = planGenerationService.validateSpatialData(spatialData);
      setValidationResult(validation);
      
      if (!validation.isValid) {
        setGenerationState('error');
        setError('Insufficient spatial data for plan generation');
      }
    }
  }, [spatialData, isVisible]);

  // Handle plan generation
  const handleGeneratePlan = useCallback(async () => {
    // Don't allow generation if already generating, cancelling, or no data
    if (!spatialData || generationState === 'generating' || isCancelling) {
      console.log('⚠️ Cannot start generation:', { generationState, isCancelling });
      return;
    }

    try {
      console.log('🌱 Starting new plan generation');
      setGenerationState('generating');
      setIsCancelling(false); // Reset cancelling flag
      setError(null);
      setProgress({ stage: 'initializing', message: 'Starting plan generation...' });
      setStreamedSections([]);
      setShowStreamingPreview(true); // Show streaming preview immediately

      // Generate the plan
      const result = await planGenerationService.generatePlantationPlan(
        spatialData,
        null, // No additional context for now
        (progressUpdate) => {
          console.log('🔄 Progress update received in UI:', progressUpdate);
          setProgress(progressUpdate);
        },
        (sectionData) => {
          // Add newly received section to the streamed sections
          setStreamedSections(prev => [...prev, sectionData]);
          console.log('📝 Section received and added to UI:', {
            section: `${sectionData.section_number}/${sectionData.total_sections}`,
            title: sectionData.title,
            percentage: sectionData.percentage,
            contentLength: sectionData.content?.length || 0
          });
        }
      );

      if (result.success) {
        setGeneratedPlan(result.plan);
        setGenerationState('completed');
        setProgress({ stage: 'completed', message: 'Plan generated successfully!' });
        
        // Show preview modal automatically
        setTimeout(() => {
          setShowPreview(true);
        }, 1000);
        
        // Notify parent component
        if (onPlanGenerated) {
          onPlanGenerated(result.plan);
        }
      } else {
        throw new Error(result.error || 'Plan generation failed');
      }

    } catch (error) {
      console.error('Plan generation error:', error);
      
      // Check if this was a user cancellation
      if (error.message === 'Generation cancelled') {
        // Silently reset to idle state without showing error
        setGenerationState('idle');
        setProgress(null);
        setError(null);
        setStreamedSections([]);
        setShowStreamingPreview(false);
      } else {
        // Show error only for actual errors (not user cancellations)
        setError(error.message);
        setGenerationState('error');
        // Clear section info and streaming sections, only show error
        setStreamedSections([]);
        setShowStreamingPreview(false);
        setProgress({ 
          stage: 'error', 
          message: error.message, 
          error: true,
          section_number: undefined,
          total_sections: undefined,
          percentage: 0
        });
      }
    }
  }, [spatialData, generationState, isCancelling, onPlanGenerated]);

  // Handle showing preview modal
  const handleShowPreview = useCallback(() => {
    setShowPreview(true);
  }, []);

  // Handle closing preview modal
  const handleClosePreview = useCallback(() => {
    setShowPreview(false);
  }, []);

  // Handle PDF download
  const handleDownloadPDF = useCallback(async (planId) => {
    // Show success message briefly
    setProgress({ stage: 'downloaded', message: 'PDF downloaded successfully!' });
    setTimeout(() => {
      if (generationState === 'completed') {
        setProgress(null);
      }
    }, 3000);
  }, [generationState]);

  // Handle minimize/maximize
  const handleMinimize = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const handleMaximize = useCallback(() => {
    setIsMinimized(false);
  }, []);

  // Handle cancellation
  const handleCancel = useCallback(async () => {
    console.log('🛑 User clicked cancel - triggering immediate cancellation');
    
    // Set cancelling flag to prevent new generations
    setIsCancelling(true);
    setGenerationState('cancelling');
    
    // Immediately update UI to show cancelling state
    setProgress({ 
      stage: 'cancelling', 
      message: 'Cancelling generation...', 
      percentage: 0 
    });
    
    // Clear streaming sections immediately
    setStreamedSections([]);
    setShowStreamingPreview(false);
    
    try {
      // Signal server-side cancellation
      await planGenerationService.cancelGeneration();
      console.log('✅ Server cancellation confirmed');
    } catch (error) {
      console.warn('⚠️ Cancellation error (non-critical):', error);
    }
    
    // Reset to idle state after cancellation is complete
    setGenerationState('idle');
    setIsCancelling(false);
    setProgress(null);
    setError(null);
    
    console.log('✅ Cancellation complete - UI reset to idle, ready for new generation');
  }, []);

  // Handle close
  const handleClose = useCallback(async () => {
    if (generationState === 'generating' || generationState === 'cancelling') {
      await handleCancel();
    }
    
    setGenerationState('idle');
    setIsCancelling(false);
    setProgress(null);
    setError(null);
    setGeneratedPlan(null);
    setShowPreview(false);
    setIsMinimized(false);
    setStreamedSections([]);
    setShowStreamingPreview(false);
    
    if (onClose) {
      onClose();
    }
  }, [generationState, handleCancel, onClose]);

  // Format spatial data summary
  const spatialSummary = spatialData ? planGenerationService.formatSpatialDataSummary(spatialData) : null;

  // If not visible and not minimized, don't render anything
  // But if minimized, always show the floating button regardless of isVisible
  if (!isVisible && !isMinimized) return null;

  return (
    <>
    {/* Floating Reopen Button - Shows when minimized */}
    {isMinimized && (
      <div className={`floating-reopen-button ${assistantOpen ? 'assistant-open' : ''}`}>
        <button onClick={handleMaximize} aria-label="Reopen Plan Generator">
          <span className="reopen-icon">🌱</span>
          <div className="reopen-content">
            <span className="reopen-title">Plan Generation</span>
            <span className="reopen-status">
              {generationState === 'generating' && progress && (
                <>
                  {progress.section_number || 0}/{progress.total_sections || 10}
                </>
              )}
              {generationState === 'completed' && '✅ Completed'}
              {generationState === 'error' && '❌ Error'}
              {generationState === 'idle' && 'Ready'}
            </span>
          </div>
          {generationState === 'generating' && (
            <div className="reopen-spinner">⟳</div>
          )}
        </button>
      </div>
    )}

    {/* Main Modal - Only show if visible or minimized */}
    {(isVisible || isMinimized) && (
      <div className={`plantation-plan-overlay ${assistantOpen ? 'assistant-open' : ''} ${isMinimized ? 'minimized' : ''}`}>
      <div className="plantation-plan-modal" style={{background: '#111827'}}>
        {/* Header */}
        <div className="plan-header">
          <h2>🌱 Generate 10-Year Plantation Plan</h2>
          <div className="header-buttons">
            <button className="minimize-button" onClick={handleMinimize} aria-label="Minimize" title="Minimize">
              −
            </button>
            <button className="close-button" onClick={handleClose} aria-label="Close" title="Close">
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="plan-content">
          {/* Location Summary */}
          {spatialSummary && (
            <div className="location-summary" style={{background: '#1f2937'}}>
              <h3 style={{color: 'white'}}>📍 Location Information</h3>
              <div className="summary-grid">
                <div className="summary-item">
                  <span className="label" style={{color: '#87a7d5'}}>Coordinates:</span>
                  <span className="value" style={{color: 'white'}}>{spatialSummary.location}</span>
                </div>
                <div className="summary-item">
                  <span className="label" style={{color: '#87a7d5'}}>Coverage Layers:</span>
                  <span className="value" style={{color: 'white'}}>{spatialSummary.coverageLayers}</span>
                </div>
                <div className="summary-item">
                  <span className="label" style={{color: '#87a7d5'}}>Nearby Features:</span>
                  <span className="value" style={{color: 'white'}}>{spatialSummary.nearbyFeatures}</span>
                </div>
                <div className="summary-item">
                  <span className="label" style={{color: '#87a7d5'}}>Data Quality:</span>
                  <span className={`value quality-${spatialSummary.dataQuality}`}>
                    {spatialSummary.dataQuality.charAt(0).toUpperCase() + spatialSummary.dataQuality.slice(1)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Validation Warnings */}
          {validationResult && (validationResult.warnings.length > 0 || validationResult.recommendations.length > 0) && (
            <div className="validation-section">
              {validationResult.warnings.length > 0 && (
                <div className="warnings">
                  <h4>⚠️ Warnings:</h4>
                  <ul>
                    {validationResult.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {validationResult.recommendations.length > 0 && (
                <div className="recommendations">
                  <h4>💡 Recommendations:</h4>
                  <ul>
                    {validationResult.recommendations.map((rec, index) => (
                      <li key={index}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Plan Features */}
          <div className="plan-features" style={{background: '#1f2937'}}>
            <h3 style={{color: 'white'}}>📋 What Your Plan Will Include:</h3>
            <div className="features-grid">
              <div className="feature-item"  style={{background: '#1f2937'}}>
                <span className="icon">🌳</span>
                <span style={{color: 'white'}}>Species-specific plantation recommendations</span>
              </div>
              <div className="feature-item" style={{background: '#1f2937'}}>
                <span className="icon">📅</span>
                <span style={{color: 'white'}}>Year-by-year implementation timeline</span>
              </div>
              <div className="feature-item" style={{background: '#1f2937'}}>
                <span className="icon">💰</span>
                <span style={{color: 'white'}}>Economic analysis and ROI projections</span>
              </div>
              <div className="feature-item" style={{background: '#1f2937'}}>
                <span className="icon">👥</span>
                <span style={{color: 'white'}}>Employment generation estimates</span>
              </div>
              <div className="feature-item" style={{background: '#1f2937'}}>
                <span className="icon">🦅</span>
                <span style={{color: 'white'}}>Wildlife and ecosystem impact assessment</span>
              </div>
              <div className="feature-item" style={{background: '#1f2937'}}>
                <span className="icon">📊</span>
                <span style={{color: 'white'}}>Visual charts and projections</span>
              </div>
              <div className="feature-item" style={{background: '#1f2937'}}>
                <span className="icon">📄</span>
                <span style={{color: 'white'}}>Professional PDF report (25+ pages)</span>
              </div>
              <div className="feature-item" style={{background: '#1f2937'}}>
                <span className="icon">🎯</span>
                <span style={{color: 'white'}}>Risk management strategies</span>
              </div>
            </div>
          </div>

          {/* Progress Section */}
          {progress && (
            <div className={`progress-section ${progress.error ? 'error' : ''}`} style={{background: '#1f2937'}}>
              <div className="progress-content">
                <div className="progress-icon">
                  {progress.stage === 'initializing' && '🔄'}
                  {(progress.stage === 'processing' || progress.stage === 'generating') && '🤖'}
                  {progress.stage === 'cancelling' && '🛑'}
                  {progress.stage === 'completed' && '✅'}
                  {progress.stage === 'downloaded' && '📄'}
                  {progress.stage === 'error' && '❌'}
                </div>
                <div className="progress-text">
                  <div className="progress-stage">
                    {progress.stage.charAt(0).toUpperCase() + progress.stage.slice(1)}
                  </div>
                  <div className="progress-message" style={{color: '#87a7d5'}}>
                    {progress.message}
                    {progress.section_number && progress.total_sections && (
                      <span style={{color: '#93c5fd', marginLeft: '8px'}}>
                        (Section {progress.section_number}/{progress.total_sections})
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {generationState === 'generating' && progress.stage !== 'error' && progress.stage !== 'cancelling' && (
                <div className="progress-bar-container">
                  <div className="progress-bar">
                    <div 
                      className="progress-bar-fill" 
                      style={{width: `${progress.percentage || 0}%`}}
                    ></div>
                  </div>
                  <div className="progress-percentage" style={{color: '#10b981', fontWeight: 'bold'}}>
                    {progress.percentage || 0}%
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Streaming Preview - Show sections as they arrive */}
          {showStreamingPreview && streamedSections.length > 0 && generationState === 'generating' && (
            <div className="streaming-preview-section" style={{
              background: '#1f2937',
              border: '2px solid #10b981',
              borderRadius: '12px',
              padding: '20px',
              marginTop: '20px',
              maxHeight: '500px',
              overflowY: 'auto'
            }}>
              <h3 style={{color: '#10b981', marginTop: 0}}>
                📝 Live Plan Generation ({streamedSections.length} sections)
              </h3>
              <div className="streamed-sections">
                {streamedSections.map((section, index) => (
                  <div key={index} className="streamed-section" style={{
                    background: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '12px',
                    animation: 'slideIn 0.3s ease-out'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '12px'
                    }}>
                      <h4 style={{
                        color: '#6ee7b7',
                        margin: 0,
                        fontSize: '1.1rem'
                      }}>
                        {section.title}
                      </h4>
                      <span style={{
                        background: '#065f46',
                        color: '#6ee7b7',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold'
                      }}>
                        Section {section.section_number}/{section.total_sections}
                      </span>
                    </div>
                    <div style={{
                      color: '#d1d5db',
                      fontSize: '0.9rem',
                      lineHeight: '1.6',
                      maxHeight: '300px',
                      overflowY: 'auto'
                    }} className="markdown-preview">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({node, ...props}) => <h1 style={{color: '#6ee7b7', fontSize: '1.5rem', marginTop: '1rem', marginBottom: '0.5rem'}} {...props} />,
                          h2: ({node, ...props}) => <h2 style={{color: '#6ee7b7', fontSize: '1.3rem', marginTop: '0.8rem', marginBottom: '0.4rem'}} {...props} />,
                          h3: ({node, ...props}) => <h3 style={{color: '#93c5fd', fontSize: '1.1rem', marginTop: '0.6rem', marginBottom: '0.3rem'}} {...props} />,
                          h4: ({node, ...props}) => <h4 style={{color: '#93c5fd', fontSize: '1rem', marginTop: '0.5rem', marginBottom: '0.2rem'}} {...props} />,
                          p: ({node, ...props}) => <p style={{marginBottom: '0.75rem', lineHeight: '1.6'}} {...props} />,
                          ul: ({node, ...props}) => <ul style={{marginLeft: '1.5rem', marginBottom: '0.75rem'}} {...props} />,
                          ol: ({node, ...props}) => <ol style={{marginLeft: '1.5rem', marginBottom: '0.75rem'}} {...props} />,
                          li: ({node, ...props}) => <li style={{marginBottom: '0.25rem'}} {...props} />,
                          strong: ({node, ...props}) => <strong style={{color: '#fbbf24', fontWeight: 'bold'}} {...props} />,
                          em: ({node, ...props}) => <em style={{color: '#a78bfa'}} {...props} />,
                          code: ({node, inline, ...props}) => inline 
                            ? <code style={{background: '#374151', padding: '2px 6px', borderRadius: '3px', fontSize: '0.85rem'}} {...props} />
                            : <code style={{display: 'block', background: '#374151', padding: '12px', borderRadius: '6px', overflowX: 'auto', marginBottom: '0.75rem'}} {...props} />,
                          blockquote: ({node, ...props}) => <blockquote style={{borderLeft: '4px solid #6ee7b7', paddingLeft: '1rem', marginLeft: '0', fontStyle: 'italic', color: '#9ca3af'}} {...props} />,
                          table: ({node, ...props}) => <table style={{borderCollapse: 'collapse', width: '100%', marginBottom: '0.75rem'}} {...props} />,
                          th: ({node, ...props}) => <th style={{border: '1px solid #374151', padding: '8px', background: '#374151', textAlign: 'left'}} {...props} />,
                          td: ({node, ...props}) => <td style={{border: '1px solid #374151', padding: '8px'}} {...props} />,
                        }}
                      >
                        {section.content.length > 1500 
                          ? section.content.substring(0, 1500) + '...' 
                          : section.content}
                      </ReactMarkdown>
                    </div>
                  </div>
                ))}
              </div>
              <style>
                {`
                  @keyframes slideIn {
                    from {
                      opacity: 0;
                      transform: translateY(20px);
                    }
                    to {
                      opacity: 1;
                      transform: translateY(0);
                    }
                  }
                `}
              </style>
            </div>
          )}

          {/* Error Display */}
          {error && generationState === 'error' && (
            <div className="error-section" style={{background: '#1f2937'}}>
              <h4>❌ Generation Failed</h4>
              <p>{error}</p>
              <p className="error-suggestion" style={{color: '#87a7d5'}}>
                Please try clicking on a different location with more available data layers.
              </p>
            </div>
          )}

          {/* Generated Plan Summary */}
          {generatedPlan && generationState === 'completed' && (
            <div className="generated-plan-summary" style={{background: '#1f2937'}}>
              <h3 style={{color: '#7ddbc0'}}>✅ Plan Generated Successfully!</h3>
              <div className="plan-details">
                <div className="plan-detail">
                  <span className="label" style={{color: '#7ddbc0'}}>Title:</span>
                  <span className="value" style={{color: '#7ddbc0'}}>{generatedPlan.title}</span>
                </div>
                <div className="plan-detail">
                  <span className="label" style={{color: '#7ddbc0'}}>Content Length:</span>
                  <span className="value" style={{color: '#7ddbc0'}}>{(generatedPlan.content?.length || 0).toLocaleString()} characters</span>
                </div>
                <div className="plan-detail">
                  <span className="label" style={{color: '#7ddbc0'}}>Generated:</span>
                  <span className="value" style={{color: '#7ddbc0'}}>
                    {new Date(generatedPlan.generated_at).toLocaleString()}
                  </span>
                </div>
                <div className="plan-detail">
                  <span className="label" style={{color: '#7ddbc0'}}>Plan ID:</span>
                  <span className="value plan-id" style={{color: "rgb(125, 219, 192)", background: '#1f2937'}}>{generatedPlan.plan_id}</span>
                </div>
              </div>
              <div className="preview-hint" style={{background: '#1f2937'}}>
                <p style={{color: 'white'}}>📋 Your plan is ready! Click "View Plan Preview" to review the content before downloading the PDF.</p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="plan-actions" style={{background: '#1f2937'}}>
          {generationState === 'idle' && validationResult?.isValid && (
            <button 
              className="generate-button primary"
              onClick={handleGeneratePlan}
              disabled={!spatialData || isCancelling}
              title={isCancelling ? 'Please wait for cancellation to complete...' : 'Generate a comprehensive 10-year plantation plan'}
            >
              {isCancelling ? '⏳ Cancelling...' : '🌱 Generate 10-Year Plan'}
            </button>
          )}

          {generationState === 'generating' && (
            <button 
              className="cancel-button secondary"
              onClick={handleCancel}
              disabled={isCancelling}
            >
              {isCancelling ? '⏳ Cancelling...' : '⏹️ Cancel Generation'}
            </button>
          )}

          {generationState === 'completed' && generatedPlan && (
            <div className="completed-actions">
              <button 
                className="preview-button primary"
                onClick={handleShowPreview}
              >
                👁️ View Plan Preview
              </button>
              <button 
                className="regenerate-button secondary"
                onClick={() => {
                  setGenerationState('idle');
                  setGeneratedPlan(null);
                  setProgress(null);
                  setShowPreview(false);
                }}
              >
                🔄 Generate New Plan
              </button>
            </div>
          )}

          {generationState === 'error' && (
            <button 
              className="retry-button secondary"
              onClick={() => {
                setGenerationState('idle');
                setError(null);
                setProgress(null);
              }}
            >
              🔄 Try Again
            </button>
          )}

          <button 
            className="close-button-bottom secondary"
            onClick={handleClose}
          >
            ✕ Close
          </button>
        </div>

        {/* Disclaimer */}
        <div className="plan-disclaimer" style={{background: '#1f2937'}}>
          <p>
            <small style={{color: 'white'}}>
              ℹ️ This AI-generated plan is based on available spatial data and Texas agricultural knowledge. 
              Please consult with local agricultural experts before implementation.
            </small>
          </p>
        </div>
      </div>
      </div>
    )}

    {/* Preview Modal */}
    <PlanPreviewModal
      planId={generatedPlan?.plan_id}
      isVisible={showPreview}
      onClose={handleClosePreview}
      onDownload={handleDownloadPDF}
      assistantOpen={assistantOpen}
    />
    </>
  );
};

export default PlantationPlanGenerator; 